import { useCallback, useEffect, useRef } from "react";

/**
 * Coach Review source toggle — a 2-button ARIA tablist with full
 * keyboard navigation matching the legacy painter's behavior
 * (`js/coaching/review.js:24-118`).
 *
 * Two modes:
 *   - "video"           → mounts the TelestratorCanvas in the stage
 *   - "tactical_board"  → mounts the TacticalBoardEditor in the stage
 *
 * Body-attribute mirror: `<body data-coach-review-source="...">` is
 * set + cleared from this component so keyboard shortcuts elsewhere
 * (M3's tactical/telestrator shortcut handlers AND the M4 save-on-S
 * shortcut) can scope themselves to the active source. The mirror
 * is critical for the M4 focus drawer, which re-parents the side
 * panel to `<body>` — breaking the normal ancestor scope. Setting
 * the attribute on `<body>` instead of the shell wrapper makes
 * CSS source-only selectors work regardless of DOM position.
 *
 * ARIA tablist semantics (WAI):
 *   - role="tablist" on the container
 *   - role="tab" on each button with aria-selected + aria-controls
 *   - Roving tabindex (active=0, inactive=-1)
 *   - ArrowLeft/Up → previous, ArrowRight/Down → next, with
 *     **automatic activation** (the tab focuses AND is selected on
 *     keypress, no Enter required — matches the legacy behavior
 *     and the WAI-ARIA pattern for tabs whose contents don't have
 *     destructive side effects)
 *   - Home → first, End → last
 *   - Click / Enter / Space activate the focused tab
 */

export type CoachReviewSource = "video" | "tactical_board";

export type SourceToggleProps = {
  value: CoachReviewSource;
  onChange: (next: CoachReviewSource) => void;
  /** ID prefix for the underlying tab buttons + tabpanels. Lets
   * a parent component wire `aria-controls` to the correct panel
   * id; defaults to "coach-review-source". */
  idPrefix?: string;
};

const TABS: Array<{ value: CoachReviewSource; label: string }> = [
  { value: "video", label: "Video" },
  { value: "tactical_board", label: "Tactical board" },
];

export function SourceToggle({ value, onChange, idPrefix = "coach-review-source" }: SourceToggleProps) {
  const buttonRefs = useRef<Record<CoachReviewSource, HTMLButtonElement | null>>({
    video: null,
    tactical_board: null,
  });

  /** Mirror the source onto `<body>` for the keyboard-shortcut +
   * focus-drawer scoping. Cleared on unmount. */
  useEffect(() => {
    document.body.dataset.coachReviewSource = value;
    return () => {
      delete document.body.dataset.coachReviewSource;
    };
  }, [value]);

  const focusAndActivate = useCallback(
    (next: CoachReviewSource) => {
      onChange(next);
      buttonRefs.current[next]?.focus();
    },
    [onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = TABS.findIndex((t) => t.value === value);
    if (currentIndex < 0) return;
    let nextIndex = currentIndex;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
        break;
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % TABS.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = TABS.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const target = TABS[nextIndex]!.value;
    focusAndActivate(target);
  };

  return (
    <div
      role="tablist"
      aria-label="Coach Review source"
      data-testid="coach-review-source-toggle"
      className="inline-flex rounded-md border border-rule bg-bg-card p-1"
    >
      {TABS.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              buttonRefs.current[tab.value] = el;
            }}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`${idPrefix}-panel`}
            id={`${idPrefix}-tab-${tab.value}`}
            tabIndex={active ? 0 : -1}
            data-coach-review-source={tab.value}
            onClick={() => onChange(tab.value)}
            onKeyDown={handleKeyDown}
            className={[
              "px-3 py-1.5 text-sm font-display uppercase tracking-wide rounded transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              active
                ? "bg-accent text-white"
                : "text-ink-muted hover:text-ink",
            ].join(" ")}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
